import type { NetworkEntry } from "../types";

const MAX_ENTRIES = 50;
const buffer: NetworkEntry[] = [];

function push(entry: NetworkEntry) {
  buffer.push(entry);
  if (buffer.length > MAX_ENTRIES) buffer.splice(0, buffer.length - MAX_ENTRIES);
}

let installed = false;

export function startNetworkCapture(opts?: { ignoreOrigin?: string }): void {
  if (installed) return;
  installed = true;

  const ignore = opts?.ignoreOrigin;

  // Pembungkus fetch. `as typeof fetch` diperlukan karena `fetch` membawa
  // properti statis (`preconnect`) yang tidak dipunyai fungsi biasa; yang kita
  // ganti hanya pemanggilannya, sisanya tetap milik peramban.
  const origFetch = window.fetch.bind(window);
  window.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    const method = (
      init?.method ?? (input instanceof Request ? input.method : "GET")
    ).toUpperCase();
    const start = performance.now();
    try {
      const res = await origFetch(input, init);
      if (!ignore || !url.startsWith(ignore)) {
        push({
          url: url.slice(0, 2048),
          method,
          status: res.status,
          durationMs: performance.now() - start,
          timestamp: Date.now(),
          type: "fetch",
        });
      }
      return res;
    } catch (err) {
      if (!ignore || !url.startsWith(ignore)) {
        push({
          url: url.slice(0, 2048),
          method,
          durationMs: performance.now() - start,
          timestamp: Date.now(),
          type: "fetch",
          error: err instanceof Error ? err.message : String(err),
        });
      }
      throw err;
    }
  }) as typeof fetch;

  // XHR wrapper
  const OrigXHR = window.XMLHttpRequest;
  function PatchedXHR(this: XMLHttpRequest) {
    const xhr = new OrigXHR();
    let method = "GET";
    let url = "";
    let start = 0;

    const origOpen = xhr.open.bind(xhr);
    xhr.open = function (m: string, u: string | URL, ...rest: unknown[]) {
      method = m.toUpperCase();
      url = typeof u === "string" ? u : u.href;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (origOpen as any)(m, u, ...rest);
    };

    const origSend = xhr.send.bind(xhr);
    xhr.send = function (...args: Parameters<XMLHttpRequest["send"]>) {
      start = performance.now();
      xhr.addEventListener("loadend", () => {
        if (ignore && url.startsWith(ignore)) return;
        push({
          url: url.slice(0, 2048),
          method,
          status: xhr.status || undefined,
          durationMs: performance.now() - start,
          timestamp: Date.now(),
          type: "xhr",
        });
      });
      return origSend(...args);
    };
    return xhr;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  window.XMLHttpRequest = PatchedXHR as any;
}

export function snapshotNetwork(): NetworkEntry[] {
  return buffer.slice();
}
